import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Check } from '@phosphor-icons/react';
import { Logo } from '@/components/Logo';

/**
 * As três linhas do painel de marca. Ficam aqui, e não num `.ts` de conteúdo:
 * são texto de uma tela só, e o arquivo separado só adiciona um salto.
 */
const SELLING_POINTS = [
  'Peças originais, com nota fiscal e transporte rastreado.',
  'Grade fechada ou peça avulsa, direto das marcas.',
  'Você vê as marcas liberadas para a sua loja — e só elas.',
];

interface AuthShellProps {
  children: ReactNode;
}

/**
 * O split editorial das telas de acesso: marca à esquerda, formulário à direita.
 *
 * **Sem `Topbar` e sem `Footer`, de propósito.** Os dois pertencem ao
 * `RootLayout` e navegam a vitrine — que é exatamente o que quem está aqui
 * ainda não pode abrir.
 *
 * **E sem alternador de tema.** Ele acompanha o sistema operacional sozinho, e
 * quem quiser trocar acha o controle na `Topbar` depois de entrar. Antes de
 * entrar, a escolha da pessoa é uma só, e um segundo controle no canto
 * competiria com ela.
 *
 * O painel esquerdo usa `bg-footer`/`text-footer-foreground`: é o único par de
 * tokens que **não** inverte entre os temas (ver `globals.css`), e é o que
 * mantém a faixa escura nos dois. Nenhum token semântico serviria — `background`
 * e `foreground` trocam de lado, e `primary` vira rosa claro no escuro.
 *
 * A parte apresentacional é separada da rota (`AuthLayout` abaixo) para caber
 * no Storybook: `<Outlet />` sem rota-pai não renderiza nada.
 */
export function AuthShell({ children }: AuthShellProps) {
  // `content-start` só no celular, e ele é a diferença entre um cabeçalho e uma
  // faixa que incha.
  //
  // Empilhado, são duas linhas `auto` dentro de um `min-h-screen`, e o default
  // do grid (`align-content: stretch`) reparte a sobra entre as duas. O efeito
  // é perverso: quanto MENOR o formulário, MAIOR a faixa preta — o passo 1 do
  // onboarding, que tem um campo só, esticava a marca para 33% da tela. Com
  // `content-start` a faixa passa a medir o próprio conteúdo, e a sobra vai
  // para o fim da página.
  //
  // No desktop precisa voltar a esticar: lá é UMA linha de duas colunas, e sem
  // o `stretch` a coluna escura pararia no meio da altura.
  return (
    <div className="grid min-h-screen grid-cols-1 content-start bg-background lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:content-stretch">
      {/* No desktop a faixa é GRUDADA e mede exatamente uma tela, nunca a
          página.

          Sem isso ela é um item de grid esticado e passa a acompanhar a altura
          do formulário — que muda a cada tecla na busca de marcas. Medido: a
          faixa ia de 900px para 1116px ao digitar e 1328px ao selecionar,
          encolhendo de volta para 1076px ao limpar. Como o conteúdo dela está
          distribuído com `justify-between`, cada mudança reposiciona logo,
          título e assinatura — é essa dança que se lê como piscada.
          `self-start` é obrigatório junto: item de grid nasce `stretch`, e
          `stretch` e `sticky` não convivem. */}
      <aside className="flex flex-col gap-8 bg-footer px-6 py-8 text-footer-foreground lg:sticky lg:top-0 lg:h-screen lg:self-start lg:justify-between lg:px-12 lg:py-14">
        {/* `text-footer-foreground` no próprio `Logo`: ele pinta com
            `currentColor` e herdaria a cor de quem o envolve. */}
        <Logo className="h-4 w-auto shrink-0 self-start text-footer-foreground" />

        <h1 className="max-w-[16ch] text-2xl font-semibold leading-tight tracking-tight lg:text-3xl">
          O estoque das marcas, a preço de saldo.
        </h1>

        {/* Some no celular junto com a assinatura: acima do formulário, a lista
            empurra o campo de senha para fora da primeira tela.

            O `hidden` é o que cumpre isso, e ele já faltou aqui uma vez: sem
            ele a regra some sem barulho, porque `<ul>` é `display: block` por
            padrão — a lista continua visível e o `flex-col`/`gap` do lado vira
            enfeite inerte, sem erro em lugar nenhum. Os 146px que ela ocupa
            levavam a faixa a 41% da tela do celular. */}
        <ul className="hidden list-none flex-col gap-3.5 lg:flex">
          {SELLING_POINTS.map((point) => (
            <li
              key={point}
              className="flex gap-3 text-[0.9375rem] leading-relaxed text-footer-foreground/75"
            >
              <Check
                size={20}
                weight="bold"
                aria-hidden
                className="mt-0.5 shrink-0 text-footer-foreground"
              />
              <span>{point}</span>
            </li>
          ))}
        </ul>

        <p className="hidden border-t border-footer-foreground/15 pt-5 text-sm text-footer-foreground/70 lg:block">
          Plataforma de saldos do Mercado Único
        </p>
      </aside>

      <main className="flex flex-col px-6 py-8 lg:justify-center lg:px-14 lg:py-14">
        <div className="mx-auto w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}

/** A rota-layout de `/login`. Ver `AuthShell` para o porquê da separação. */
export function AuthLayout() {
  return (
    <AuthShell>
      <Outlet />
    </AuthShell>
  );
}
